/**
 * Listing memory - keeps a listing's filters, search, sort and page for the life of
 * the browser tab, so leaving a list (to open a record, or anywhere else) and coming
 * back puts it back exactly as it was instead of starting from blank.
 *
 * sessionStorage, not localStorage: the state belongs to this tab's working session.
 * It survives moving around the console and a page refresh; a new tab or a closed
 * browser starts clean. Every key is wiped when the session ends (logout or expiry,
 * see AuthService.clearToken), so one admin's filters never reach the next person to
 * sign in at the same browser.
 *
 * Each listing owns the shape of its snapshot and passes a `version`; bump it when the
 * shape changes, so a snapshot written by an older build is ignored rather than
 * half-applied. Storage can be unavailable (private mode, quota, blocked site data),
 * so every access is guarded and the listing simply opens with its defaults.
 */

const PREFIX = 'gc.list-state.';

interface Stored<T> {
  v: number;
  state: T;
}

/** Saves a listing's snapshot under `key` (e.g. 'quotes', 'account-quotes:6'). */
export function saveListState(key: string, version: number, state: object): void {
  try {
    sessionStorage.setItem(PREFIX + key, JSON.stringify({v: version, state} as Stored<object>));
  } catch {
    // No storage: the listing still works, it just won't remember.
  }
}

/** The snapshot saved under `key`, or null when there is none (or it is from another version). */
export function readListState<T>(key: string, version: number): T | null {
  try {
    const raw = sessionStorage.getItem(PREFIX + key);
    if (!raw) {
      return null;
    }
    const stored = JSON.parse(raw) as Stored<T>;
    return stored && stored.v === version && stored.state ? stored.state : null;
  } catch {
    return null;
  }
}

/** Forgets every listing's snapshot - called when the session ends. */
export function clearListStates(): void {
  try {
    Object.keys(sessionStorage)
      .filter(key => key.startsWith(PREFIX))
      .forEach(key => sessionStorage.removeItem(key));
  } catch {
    // Nothing stored, or storage blocked - either way nothing to clear.
  }
}

/**
 * A saved date range. JSON turns Dates into ISO strings, so ranges are stored as
 * strings and revived with {@link reviveRange}. `key` is the date filter's preset
 * ('This month', 'Custom', ...), kept so the picker reopens on the same choice.
 */
export interface SavedRange {
  key: string | null;
  from: string;
  to: string;
}

/** Packs a range for storage; null stays null. */
export function packRange(range: { from: Date; to: Date } | null, key: string | null): SavedRange | null {
  return range ? {key, from: range.from.toISOString(), to: range.to.toISOString()} : null;
}

/** Unpacks a stored range back into Dates; anything malformed comes back as null. */
export function reviveRange(saved: SavedRange | null | undefined): { from: Date; to: Date } | null {
  if (!saved?.from || !saved?.to) {
    return null;
  }
  const from = new Date(saved.from);
  const to = new Date(saved.to);
  return isNaN(from.getTime()) || isNaN(to.getTime()) ? null : {from, to};
}
